import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Image } from 'src/components/image';
import { SparkAdminPaymentMethodDto } from '../../../../../libs/contracts/src';
import { useEffect } from 'react';

// Type alias to ensure id property is recognized (needed because JSON deserialization doesn't preserve class inheritance)
type PaymentMethodWithId = SparkAdminPaymentMethodDto & {
  id: number;
  requirePaymentScreenshot: boolean;
};

type Props = {
  selectedMethodId: number | null;
  onSelect: (methodId: number, requirePaymentScreenshot: boolean) => void;

  paymentMethods?: PaymentMethodWithId[];
};

export default function PaymentMethodList({
  selectedMethodId,
  onSelect,
  paymentMethods = [],
}: Props) {
  // Auto-select if only one payment method exists
  useEffect(() => {
    if (paymentMethods.length === 1) {
      onSelect(
        paymentMethods[0]?.id,
        Boolean(paymentMethods[0]?.requirePaymentScreenshot),
      );
    }
  }, [paymentMethods, onSelect]);

  if (paymentMethods.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5} direction="column">
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Select Payment Method
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          value={selectedMethodId?.toString() || ''}
          onChange={(e) => {
            const selectedId = Number(e.target.value);
            //id to backend
            const selectedMethod = paymentMethods.find(
              (pm) => pm.id === selectedId,
            );
            onSelect(
              selectedId,
              Boolean(selectedMethod?.requirePaymentScreenshot),
            );
          }}
        >
          {paymentMethods.map((method) => (
            <Box
              key={method.id}
              sx={{
                border: 1,
                borderColor:
                  selectedMethodId === method.id ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelect(method.id, Boolean(method?.requirePaymentScreenshot));
              }}
            >
              <FormControlLabel
                value={method?.id.toString()}
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Image
                      src={method.provider.icon.link}
                      sx={{
                        height: 24,
                        '& img': {
                          objectFit: 'contain',
                          width: 'auto',
                          height: '100%',
                        },
                      }}
                    />
                    <Typography variant="subtitle1">
                      {method.provider.name}
                    </Typography>
                  </Stack>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>
    </Stack>
  );
}
